// Time Tracking API Service
import axios, { AxiosInstance } from "axios";
import {
  TimeEntry,
  EmployeeStatus,
  TimesheetReport,
  TimeInRequest,
  TimeOutRequest,
  BreakStartRequest,
  BreakEndRequest,
  BreakRecord,
  TimeTrackingDashboard,
  AdminDashboard,
  ApiResponse,
  DateRange,
} from "../types/timetracking";
import { todayUtc } from "./dateTimeService";

class TimeTrackingApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: "/ofc",
      withCredentials: true,
    });
  }

  // Employee Time Tracking Operations

  async timeIn(request: TimeInRequest): Promise<TimeEntry> {
    const response = await this.api.post<ApiResponse<TimeEntry>>(
      `/timetracking/time-in`,
      null,
      {
        params: {
          employeeId: request.employeeId,
          employeeName: request.employeeName,
          notes: request.notes,
          timezone:
            request.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to record time in");
    }

    return response.data.data;
  }

  async timeOut(request: TimeOutRequest): Promise<TimeEntry> {
    const response = await this.api.post<ApiResponse<TimeEntry>>(
      `/timetracking/time-out`,
      null,
      {
        params: {
          employeeId: request.employeeId,
          notes: request.notes,
          timezone:
            request.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to record time out");
    }

    return response.data.data;
  }

  // Break Operations

  async startBreak(request: BreakStartRequest): Promise<TimeEntry> {
    const response = await this.api.post<ApiResponse<TimeEntry>>(
      `/timetracking/break-start`,
      null,
      {
        params: {
          employeeId: request.employeeId,
          notes: request.notes,
          timezone:
            request.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to start break");
    }

    return response.data.data;
  }

  async endBreak(request: BreakEndRequest): Promise<TimeEntry> {
    const response = await this.api.post<ApiResponse<TimeEntry>>(
      `/timetracking/break-end`,
      null,
      {
        params: {
          employeeId: request.employeeId,
          timezone:
            request.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to end break");
    }

    return response.data.data;
  }

  async isOnBreak(employeeId: number): Promise<boolean> {
    const response = await this.api.get<ApiResponse<boolean>>(
      `/timetracking/is-on-break/${employeeId}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to check break status");
    }

    return response.data.data || false;
  }

  async getEmployeeStatus(
    employeeId: number,
    timezone?: string,
  ): Promise<EmployeeStatus> {
    const params = new URLSearchParams();
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      params.append("timezone", tz);
    }

    const response = await this.api.get<ApiResponse<EmployeeStatus>>(
      `/timetracking/status/${employeeId}?${params.toString()}`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get employee status");
    }

    return response.data.data;
  }

  async isTimedIn(employeeId: number): Promise<boolean> {
    const response = await this.api.get<ApiResponse<boolean>>(
      `/timetracking/is-timed-in/${employeeId}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to check timed in status");
    }

    return response.data.data || false;
  }

  // Personal Data Retrieval

  async getTimeEntriesForDate(
    employeeId: number,
    date: string,
  ): Promise<TimeEntry[]> {
    const response = await this.api.get<ApiResponse<TimeEntry[]>>(
      `/timetracking/entries/${employeeId}/date/${date}`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get time entries");
    }

    return response.data.data;
  }

  async getTimeEntriesForDateRange(
    employeeId: number,
    startDate: string,
    endDate: string,
  ): Promise<TimeEntry[]> {
    const response = await this.api.get<ApiResponse<TimeEntry[]>>(
      `/timetracking/entries/${employeeId}/range`,
      {
        params: { startDate, endDate },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get time entries");
    }

    return response.data.data;
  }

  async getPersonalTimesheetReport(
    employeeId: number,
    startDate: string,
    endDate: string,
  ): Promise<TimesheetReport> {
    const response = await this.api.get<ApiResponse<TimesheetReport>>(
      `/timetracking/timesheet/${employeeId}`,
      {
        params: { startDate, endDate },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get timesheet report");
    }

    return response.data.data;
  }

  // Real-time Status Monitoring

  async getAllEmployeeStatuses(timezone?: string): Promise<EmployeeStatus[]> {
    const params = new URLSearchParams();

    // Use provided timezone or detect from browser
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      params.append("timezone", tz);
    }

    const response = await this.api.get<ApiResponse<EmployeeStatus[]>>(
      `/timetracking/status/all?${params.toString()}`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get employee statuses");
    }

    return response.data.data;
  }

  async getCurrentlyWorkingEmployees(): Promise<EmployeeStatus[]> {
    const response = await this.api.get<ApiResponse<EmployeeStatus[]>>(
      `/timetracking/status/working`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get working employees");
    }

    return response.data.data;
  }

  // Convenience Methods

  async getTodayTimeEntries(employeeId: number): Promise<TimeEntry[]> {
    const response = await this.api.get<ApiResponse<TimeEntry[]>>(
      `/timetracking/entries/${employeeId}/today`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to get today's time entries",
      );
    }

    return response.data.data;
  }

  async getThisWeekTimeEntries(employeeId: number): Promise<TimeEntry[]> {
    const response = await this.api.get<ApiResponse<TimeEntry[]>>(
      `/timetracking/entries/${employeeId}/this-week`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to get this week's time entries",
      );
    }

    return response.data.data;
  }

  async getThisMonthTimeEntries(employeeId: number): Promise<TimeEntry[]> {
    const response = await this.api.get<ApiResponse<TimeEntry[]>>(
      `/timetracking/entries/${employeeId}/this-month`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to get this month's time entries",
      );
    }

    return response.data.data;
  }

  // Dashboard Data

  async getTimeTrackingDashboard(
    employeeId: number,
  ): Promise<TimeTrackingDashboard> {
    try {
      const [currentStatus, todayEntries, weekEntries, monthEntries] =
        await Promise.all([
          this.getEmployeeStatus(employeeId),
          this.getTodayTimeEntries(employeeId),
          this.getThisWeekTimeEntries(employeeId),
          this.getThisMonthTimeEntries(employeeId),
        ]);

      // Calculate week summary
      const weekTotalHours = weekEntries.reduce(
        (sum, entry) => sum + entry.totalHours,
        0,
      );
      const weekUniqueDays = new Set(weekEntries.map((entry) => entry.workDate))
        .size;

      // Calculate month summary
      const monthTotalHours = monthEntries.reduce(
        (sum, entry) => sum + entry.totalHours,
        0,
      );
      const monthUniqueDays = new Set(
        monthEntries.map((entry) => entry.workDate),
      ).size;

      return {
        currentStatus,
        todayEntries,
        weekSummary: {
          totalHours: weekTotalHours,
          totalDays: weekUniqueDays,
          averageHours:
            weekUniqueDays > 0 ? weekTotalHours / weekUniqueDays : 0,
        },
        monthSummary: {
          totalHours: monthTotalHours,
          totalDays: monthUniqueDays,
          averageHours:
            monthUniqueDays > 0 ? monthTotalHours / monthUniqueDays : 0,
        },
        recentActivity: [...weekEntries]
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .slice(0, 10),
      };
    } catch (_) {
      throw new Error("Failed to load dashboard data");
    }
  }

  // Admin Operations

  async getAllTimesheetReports(
    startDate: string,
    endDate: string,
  ): Promise<TimesheetReport[]> {
    const response = await this.api.get<ApiResponse<TimesheetReport[]>>(
      `/timetracking/admin/reports/all`,
      {
        params: { startDate, endDate },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get timesheet reports");
    }

    return response.data.data;
  }

  async getEmployeeTimesheetReport(
    employeeId: number,
    startDate: string,
    endDate: string,
  ): Promise<TimesheetReport> {
    const response = await this.api.get<ApiResponse<TimesheetReport>>(
      `/timetracking/admin/reports/employee/${employeeId}`,
      {
        params: { startDate, endDate },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to get employee timesheet report",
      );
    }

    return response.data.data;
  }

  async getOverallSummaryReport(
    startDate: string,
    endDate: string,
  ): Promise<TimesheetReport> {
    const response = await this.api.get<ApiResponse<TimesheetReport>>(
      `/timetracking/admin/reports/summary`,
      {
        params: { startDate, endDate },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get summary report");
    }

    return response.data.data;
  }

  async forceTimeOut(
    employeeId: number,
    adminNotes?: string,
  ): Promise<TimeEntry> {
    const response = await this.api.post<ApiResponse<TimeEntry>>(
      `/timetracking/admin/force-time-out/${employeeId}`,
      null,
      {
        params: { adminNotes },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to force time out");
    }

    return response.data.data;
  }

  async updateTimeEntry(
    entryId: number,
    timeEntry: Partial<TimeEntry>,
  ): Promise<TimeEntry> {
    const response = await this.api.put<ApiResponse<TimeEntry>>(
      `/timetracking/admin/entries/${entryId}`,
      timeEntry,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update time entry");
    }

    return response.data.data;
  }

  async deleteTimeEntry(entryId: number): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(
      `/timetracking/admin/entries/${entryId}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to delete time entry");
    }
  }

  async bulkForceTimeOut(
    employeeIds: number[],
    adminNotes?: string,
  ): Promise<TimeEntry[]> {
    const response = await this.api.post<ApiResponse<TimeEntry[]>>(
      `/timetracking/admin/bulk/force-time-out`,
      employeeIds,
      {
        params: { adminNotes },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to perform bulk force time out",
      );
    }

    return response.data.data;
  }

  async exportTimesheetDataAsCsv(
    startDate: string,
    endDate: string,
    employeeId?: number,
    timezone?: string,
  ): Promise<string> {
    const params: any = { startDate, endDate };
    if (employeeId !== undefined) {
      params.employeeId = employeeId;
    }
    if (timezone) {
      params.timezone = timezone;
    }

    const response = await this.api.get(`/timetracking/admin/export/csv`, {
      params,
      responseType: "text",
    });

    return response.data;
  }

  // Utility method to get admin dashboard data
  async getAdminDashboard(dateRange?: DateRange): Promise<AdminDashboard> {
    const startDate = dateRange?.startDate || todayUtc();
    const endDate = dateRange?.endDate || todayUtc();

    try {
      const [allStatuses, summaryReport] = await Promise.all([
        this.getAllEmployeeStatuses(),
        this.getOverallSummaryReport(startDate, endDate),
      ]);

      const currentlyWorking = allStatuses.filter(
        (status) => status.currentStatus === "IN",
      ).length;

      return {
        totalEmployees: allStatuses.length,
        currentlyWorking,
        totalHoursToday: summaryReport.totalHours,
        averageHoursPerEmployee: summaryReport.averageHoursPerDay,
        topWorkers: [], // This would need additional API endpoint
        departmentSummary: [], // This would need additional API endpoint
      };
    } catch (_) {
      throw new Error("Failed to load admin dashboard data");
    }
  }

  // Admin Timesheet Management

  async updateTimeEntryAsAdmin(
    entryId: number,
    timeIn: string | null,
    timeOut: string | null,
    adminNote: string,
  ): Promise<TimeEntry> {
    const response = await this.api.put<ApiResponse<TimeEntry>>(
      `/timetracking/admin/timesheet/${entryId}`,
      null,
      {
        params: {
          timeIn: timeIn,
          timeOut: timeOut,
          adminNote: adminNote,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update time entry");
    }

    return response.data.data;
  }

  async createTimeEntryAsAdmin(
    employeeId: number,
    timeIn: string,
    timeOut: string | null,
    adminNote: string,
  ): Promise<TimeEntry> {
    const response = await this.api.post<ApiResponse<TimeEntry>>(
      `/timetracking/admin/timesheet`,
      null,
      {
        params: {
          employeeId: employeeId,
          timeIn: timeIn,
          timeOut: timeOut,
          adminNote: adminNote,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to create time entry");
    }

    return response.data.data;
  }

  // Admin Break Record Management

  async getBreakRecordsForTimeEntry(
    timeEntryId: number,
  ): Promise<BreakRecord[]> {
    const response = await this.api.get<ApiResponse<BreakRecord[]>>(
      `/timetracking/admin/timesheet/${timeEntryId}/breaks`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to get break records");
    }

    return response.data.data;
  }

  async addBreakRecordAsAdmin(
    timeEntryId: number,
    breakStart: string,
    breakEnd: string | null,
    notes: string | null,
    adminNote: string,
  ): Promise<BreakRecord> {
    const response = await this.api.post<ApiResponse<BreakRecord>>(
      `/timetracking/admin/timesheet/${timeEntryId}/breaks`,
      null,
      {
        params: {
          breakStart,
          breakEnd,
          notes,
          adminNote,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to add break record");
    }

    return response.data.data;
  }

  async updateBreakRecordAsAdmin(
    breakRecordId: number,
    breakStart: string,
    breakEnd: string | null,
    notes: string | null,
    adminNote: string,
  ): Promise<BreakRecord> {
    const response = await this.api.put<ApiResponse<BreakRecord>>(
      `/timetracking/admin/breaks/${breakRecordId}`,
      null,
      {
        params: {
          breakStart,
          breakEnd,
          notes,
          adminNote,
        },
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update break record");
    }

    return response.data.data;
  }

  async deleteBreakRecordAsAdmin(
    breakRecordId: number,
    adminNote: string,
  ): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(
      `/timetracking/admin/breaks/${breakRecordId}`,
      {
        params: {
          adminNote,
        },
      },
    );

    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to delete break record");
    }
  }
}

export const timeTrackingApi = new TimeTrackingApiService();
